/**
 * Map view for a travel trip. Plots one pin per day (placed at the day's
 * geocoded coordinates) and one pin per event that has coordinates. Days are
 * connected by a solid polyline in day-order; transit segments are drawn as a
 * dashed line between the surrounding days.
 *
 * The view is a feature — when the `map` flag is off the caller must not
 * render it, so this file never has to check the flag itself.
 *
 * Leaflet notes:
 *   - Vite's asset pipeline breaks Leaflet's built-in default icon URL lookup,
 *     so we replace the default icon with explicit CDN URLs once at module load.
 *   - `leaflet/dist/leaflet.css` is a side-effect import — bundlers include it
 *     only in client builds. The SSR guard around the icon patch is defensive
 *     so any future prerender step wouldn't blow up.
 */

import { useMemo } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { ChevronDown, Loader2, MapPin, RefreshCw } from 'lucide-react';
import type { TravelEventResponse, TravelItemResponse, TravelTripResponse } from '../api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/use-toast';
import { useTravelMapGenerator } from '../hooks/useTravelMapGenerator';
import { getDayColor, formatDayDate } from './TravelHelpers';

// Replace Leaflet's default icon URLs (Vite-safe CDN fallbacks). This is a
// one-time side effect and is skipped on the server where `L` / the DOM aren't
// meaningfully available.
if (typeof window !== 'undefined') {
  // @ts-expect-error Internal Leaflet symbol — documented workaround for Vite.
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  });
}

interface TravelMapViewProps {
  trip: TravelTripResponse;
  items: TravelItemResponse[];
  events: TravelEventResponse[];
  activeItemId: string | null;
  onSelectItem: (itemId: string) => void;
}

interface DayMarker {
  item: TravelItemResponse;
  lat: number;
  lng: number;
  color: string;
}

interface EventMarker {
  event: TravelEventResponse;
  item: TravelItemResponse;
  lat: number;
  lng: number;
  color: string;
}

/**
 * Circular DivIcon so marker color matches the day palette. Using a DivIcon
 * avoids shipping a sprite per color and stays crisp at any zoom.
 */
function buildDayIcon(color: string, label: string, highlighted: boolean): L.DivIcon {
  const size = highlighted ? 30 : 24;
  const border = highlighted ? '3px solid #ffffff' : '2px solid #ffffff';
  const shadow = highlighted
    ? '0 0 0 2px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.35)'
    : '0 1px 4px rgba(0,0,0,0.4)';
  return L.divIcon({
    className: 'travel-map-day-marker',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:${border};box-shadow:${shadow};color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;">${label}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function buildEventIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: 'travel-map-event-marker',
    html: `<div style="width:12px;height:12px;border-radius:50%;background:${color};border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
}

/** Fits the map to whatever markers are present, re-centering when they change. */
function FitBoundsEffect({ points }: { points: Array<[number, number]> }) {
  const map = useMap();
  if (points.length === 0) return null;
  if (points.length === 1) {
    map.setView(points[0], 11);
  } else {
    map.fitBounds(points, { padding: [40, 40] });
  }
  return null;
}

export function TravelMapView({
  trip,
  items,
  events,
  activeItemId,
  onSelectItem,
}: TravelMapViewProps) {
  const { generatePins, isGenerating } = useTravelMapGenerator(trip.tripId);

  // How many rows are still missing coordinates? Used to enable/disable the
  // "Generate pins" button and to phrase its tooltip.
  const missingCoordCounts = useMemo(() => {
    let days = 0;
    let eventsMissing = 0;
    for (const it of items) {
      if (it.itemType === 'day' && (it.lat == null || it.lng == null)) days += 1;
    }
    for (const e of events) {
      if (e.lat == null || e.lng == null) eventsMissing += 1;
    }
    return { days, events: eventsMissing, total: days + eventsMissing };
  }, [items, events]);

  const runGeneratePins = async (options: { force?: boolean } = {}) => {
    const result = await generatePins(items, events, options);
    if (result.attempted === 0 && result.skipped === 0) {
      toast({ title: 'All pins already placed', description: 'Nothing to generate.' });
      return;
    }
    const parts: string[] = [];
    if (result.succeeded > 0) parts.push(`${result.succeeded} placed`);
    if (result.failed > 0) parts.push(`${result.failed} not found`);
    if (result.skipped > 0) parts.push(`${result.skipped} skipped (no city/address)`);
    toast({
      title: result.succeeded > 0 ? 'Map updated' : 'Generate pins finished',
      description: parts.join(' · ') || 'No changes.',
    });
  };

  const dayMarkers = useMemo<DayMarker[]>(() => {
    return items
      .filter((i) => i.itemType === 'day' && i.lat != null && i.lng != null)
      .map((i) => ({
        item: i,
        lat: i.lat as number,
        lng: i.lng as number,
        color: getDayColor(i.dayNumber ?? 1),
      }));
  }, [items]);

  const eventMarkers = useMemo<EventMarker[]>(() => {
    const itemsById = new Map(items.map((i) => [i.itemId, i]));
    return events
      .filter((e) => e.lat != null && e.lng != null)
      .map((e) => {
        const item = itemsById.get(e.itemId);
        if (!item) return null;
        return {
          event: e,
          item,
          lat: e.lat as number,
          lng: e.lng as number,
          color: getDayColor(item.dayNumber ?? 1),
        };
      })
      .filter((m): m is EventMarker => m !== null);
  }, [items, events]);

  // Solid polylines: connect each day's events in their recorded order, so the
  // user can see the within-day path.
  const eventPolylines = useMemo(() => {
    const byItem = new Map<string, EventMarker[]>();
    for (const em of eventMarkers) {
      const list = byItem.get(em.item.itemId) ?? [];
      list.push(em);
      byItem.set(em.item.itemId, list);
    }
    const lines: Array<{ key: string; color: string; points: Array<[number, number]> }> = [];
    for (const [itemId, markers] of byItem) {
      if (markers.length < 2) continue;
      const sorted = markers.slice().sort((a, b) => a.event.sortOrder - b.event.sortOrder);
      lines.push({
        key: `day-${itemId}`,
        color: sorted[0].color,
        points: sorted.map((m) => [m.lat, m.lng]),
      });
    }
    return lines;
  }, [eventMarkers]);

  // Dashed transit polylines: for each transit item, draw from the previous
  // day's pin to the next day's pin (skipping over the transit row itself).
  const transitPolylines = useMemo(() => {
    const lines: Array<{ key: string; points: Array<[number, number]>; color: string }> = [];
    const dayByItemId = new Map(dayMarkers.map((d) => [d.item.itemId, d]));

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.itemType !== 'transit') continue;
      // Find previous day with coords
      let prevDay: DayMarker | undefined;
      for (let j = i - 1; j >= 0; j--) {
        const found = dayByItemId.get(items[j].itemId);
        if (found) {
          prevDay = found;
          break;
        }
      }
      // Find next day with coords
      let nextDay: DayMarker | undefined;
      for (let j = i + 1; j < items.length; j++) {
        const found = dayByItemId.get(items[j].itemId);
        if (found) {
          nextDay = found;
          break;
        }
      }
      if (prevDay && nextDay) {
        lines.push({
          key: `transit-${item.itemId}`,
          points: [
            [prevDay.lat, prevDay.lng],
            [nextDay.lat, nextDay.lng],
          ],
          color: '#6b7280',
        });
      }
    }

    return lines;
  }, [items, dayMarkers]);

  const allPoints = useMemo<Array<[number, number]>>(() => {
    const pts: Array<[number, number]> = [];
    for (const d of dayMarkers) pts.push([d.lat, d.lng]);
    for (const e of eventMarkers) pts.push([e.lat, e.lng]);
    return pts;
  }, [dayMarkers, eventMarkers]);

  if (dayMarkers.length === 0 && eventMarkers.length === 0) {
    const hasAnySourceText = items.some(
      (i) => i.itemType === 'day' && (i.city || i.accommodationAddress),
    );
    return (
      <div className="flex items-center justify-center h-full text-center p-8">
        <div className="max-w-sm space-y-3">
          <p className="text-sm font-medium">No coordinates yet</p>
          <p className="text-xs text-muted-foreground">
            Add a city and country to a day (or an address to an event) and the map will place a pin
            automatically. You can also re-trigger the lookup from the day editor.
          </p>
          {hasAnySourceText && (
            <Button size="sm" onClick={() => void runGeneratePins()} disabled={isGenerating}>
              {isGenerating ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <MapPin className="h-3 w-3 mr-1" /> Generate pins from entries
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const initialCenter: [number, number] =
    dayMarkers[0] != null
      ? [dayMarkers[0].lat, dayMarkers[0].lng]
      : [eventMarkers[0].lat, eventMarkers[0].lng];

  return (
    <div className="h-full w-full relative">
      {/* Floating "Generate pins" control. Leaflet panes are z-index 200–700;
          z-50 in tailwind is 50, so bump the wrapper with an inline style. */}
      <div className="absolute top-3 right-3 flex" style={{ zIndex: 1000 }}>
        <Button
          size="sm"
          variant="secondary"
          className="shadow-md rounded-r-none"
          onClick={() => void runGeneratePins()}
          disabled={isGenerating || missingCoordCounts.total === 0}
          title={
            missingCoordCounts.total === 0
              ? 'All days and events already have pins — use Regenerate to overwrite'
              : `Geocode ${missingCoordCounts.total} missing ${
                  missingCoordCounts.total === 1 ? 'pin' : 'pins'
                } from day/event text`
          }
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating…
            </>
          ) : (
            <>
              <MapPin className="h-3 w-3 mr-1" />
              {missingCoordCounts.total === 0
                ? 'All pins placed'
                : `Generate ${missingCoordCounts.total} pin${missingCoordCounts.total === 1 ? '' : 's'}`}
            </>
          )}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="secondary"
              className="shadow-md rounded-l-none border-l border-border px-2"
              disabled={isGenerating}
              title="More options"
            >
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => void runGeneratePins({ force: true })}>
              <RefreshCw className="h-4 w-4 mr-2" /> Regenerate all pins
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <MapContainer
        center={initialCenter}
        zoom={6}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBoundsEffect points={allPoints} />

        {transitPolylines.map((line) => (
          <Polyline
            key={line.key}
            positions={line.points}
            pathOptions={{ color: line.color, weight: 2, dashArray: '6 6', opacity: 0.7 }}
          />
        ))}

        {eventPolylines.map((line) => (
          <Polyline
            key={line.key}
            positions={line.points}
            pathOptions={{ color: line.color, weight: 3, opacity: 0.6 }}
          />
        ))}

        {dayMarkers.map((dm) => {
          const highlighted = dm.item.itemId === activeItemId;
          const label = String(dm.item.dayNumber ?? '');
          return (
            <Marker
              key={dm.item.itemId}
              position={[dm.lat, dm.lng]}
              icon={buildDayIcon(dm.color, label, highlighted)}
              eventHandlers={{ click: () => onSelectItem(dm.item.itemId) }}
            >
              <Popup>
                <div className="text-sm">
                  <div className="font-semibold">
                    Day {dm.item.dayNumber}
                    {dm.item.dayTitle ? ` — ${dm.item.dayTitle}` : ''}
                  </div>
                  <div className="text-xs opacity-70">
                    {formatDayDate(trip.startDate, dm.item.dayNumber ?? 1)}
                  </div>
                  {dm.item.city ? (
                    <div className="text-xs mt-1">
                      {dm.item.city}
                      {dm.item.country ? `, ${dm.item.country}` : ''}
                    </div>
                  ) : null}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {eventMarkers.map((em) => (
          <Marker
            key={em.event.eventId}
            position={[em.lat, em.lng]}
            icon={buildEventIcon(em.color)}
            eventHandlers={{ click: () => onSelectItem(em.item.itemId) }}
          >
            <Popup>
              <div className="text-sm">
                <div className="font-semibold">{em.event.title}</div>
                {em.event.time ? <div className="text-xs opacity-70">{em.event.time}</div> : null}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
